'use client'

import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useModal } from '@/blocks/redesign_blocks/Modal/ModalContext'
import AnimatedSection from '@/components/AnimatedSection'
import useEmblaCarousel from 'embla-carousel-react'
import styles from './styles.module.css'

// Категория карточки (совпадает с названием бейджа)
type Category = 'Финансы' | 'ИИ' | 'Производство' | 'Продажи' | 'Медицина'

// Цвета бейджей заведены CSS-переменными в globals.css — здесь только маппинг на классы-модификаторы
const BADGE_CLASS: Record<Category, string> = {
	'Финансы': styles.badgeFinance,
	'ИИ': styles.badgeAi,
	'Производство': styles.badgeProduction,
	'Продажи': styles.badgeSales,
	'Медицина': styles.badgeMedicine
}

// Табы категорий. «Все» — всегда первый.
const CATEGORIES: string[] = ['Все', 'Финансы', 'ИИ', 'Медицина', 'Продажи', 'Производство']

// На планшете и мобилке (<=1439) показываем карточки постранично: по PAGE_SIZE за один клик
const PAGE_SIZE = 6

interface ProductCard {
	title: string
	description: string
	badges: Category[]
	image: string
	href: string | null
}

// ЗАГЛУШКИ: тексты сняты с макета, картинки временно ссылаются на существующие svg — заменить на свои
const cards: ProductCard[] = [
	{
		title: 'B2B: платежи от ИП и юрлиц',
		description:
			'Принимайте онлайн-платежи от юридических лиц и ИП на вашем сайте или по ссылке. Превратите 3 дня оплаты в 3 минуты.',
		badges: ['Финансы'],
		image: '/images/it-solutions/it_product_1.svg',
		href: '/it-solutions/b2b/'
	},
	{
		title: 'B2C: выплаты физлицам с бизнес-карты',
		description:
			'Переводы физическим лицам с бизнес-карт. Осуществляйте расчёты через браузер любого устройства без внедрения банковского ПО.',
		badges: ['Финансы'],
		image: '/images/it-solutions/it_product_2.svg',
		href: '/it-solutions/b2c/'
	},
	{
		title: 'Кредитный портфель для предприятий',
		description:
			'Автоматизация работы с кредитами — от подачи заявки до финальной оплаты. Сервис для отслеживания остатков долга, процентов и сбор документов.',
		badges: ['Финансы', 'ИИ'],
		image: '/images/it-solutions/it_product_3.svg',
		href: null
	},
	{
		title: 'Telegram-бот Строй Контроль',
		description:
			'Бот, который автоматизирует подготовку строительных предписаний на базе ИИ. Он преобразует голосовые сообщения и фото в структурированные документы',
		badges: ['Производство', 'ИИ'],
		image: '/images/it-solutions/it_product_4.svg',
		href: null
	},
	{
		title: 'Управление конкурентными листами',
		description:
			'Cокращаем подготовку конкурентных листов с 5 дней до 1 часа. Автоматизируем выбор поставщиков и контроль цен. Превращаем хаос в прозрачный процесс.',
		badges: ['Производство', 'ИИ'],
		image: '/images/it-solutions/it_product_5.svg',
		href: '/services/competition-sheet-system'
	},
	{
		title: 'Система планирования и контроля производства',
		description:
			'Сервис связывает работу цехов, складов и магазинов для точного планирования выпуска продукции. Система повышает точность прогнозов, избавляя от излишков.',
		badges: ['Производство', 'ИИ'],
		image: '/images/it-solutions/it_product_6.svg',
		href: null
	},
	{
		title: 'Система внутреннего документооборота',
		description:
			'Сервис связывает работу цехов, складов и магазинов для точного планирования выпуска продукции. Система повышает точность прогнозов, избавляя от излишков.',
		badges: ['Производство', 'ИИ'],
		image: '/images/it-solutions/it_product_7.svg',
		href: null
	},
	{
		title: 'Динамическое ценообразование',
		description:
			'Управление стоимостью товаров на основе спроса, остатков и цен конкурентов. Перейдите от ручного обновления прайсов к обоснованным ИИ-рекомендациям',
		badges: ['Продажи', 'ИИ'],
		image: '/images/it-solutions/it_product_8.svg',
		href: null
	},
	{
		title: 'Распознавание речи для врачей',
		description:
			'Сервис распознавания речи для врача и автоматическое формирование медицинских документов для дальнейшей отправки в МИС',
		badges: ['Медицина', 'ИИ'],
		image: '/images/it-solutions/it_product_9.svg',
		href: null
	}
]

const ItProduct: React.FC = () => {
	const router = useRouter()
	const { openConsultationModal } = useModal()
	const [activeCategory, setActiveCategory] = useState<string>('Все')
	const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE)
	const [windowWidth, setWindowWidth] = useState<number>(
		typeof window !== 'undefined' ? window.innerWidth : 1440
	)

	const [emblaRef] = useEmblaCarousel({
		dragFree: true,
		align: 'start'
	})

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth)
		handleResize()
		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	// Сбрасываем пагинацию при смене категории
	useEffect(() => {
		setVisibleCount(PAGE_SIZE)
	}, [activeCategory])

	// Планшет и мобилка
	const isCompact = windowWidth <= 1439

	const filteredCards =
		activeCategory === 'Все'
			? cards
			: cards.filter((card) => card.badges.includes(activeCategory as Category))

	const visibleCards = isCompact ? filteredCards.slice(0, visibleCount) : filteredCards
	const showButton = isCompact && visibleCount < filteredCards.length

	// По клику показываем ещё PAGE_SIZE карточек, либо все оставшиеся, если их меньше PAGE_SIZE
	const handleShowMore = () => {
		setVisibleCount((prev) => {
			const remaining = filteredCards.length - prev
			return remaining < PAGE_SIZE ? filteredCards.length : prev + PAGE_SIZE
		})
	}

	const handleCardClick = (href: string | null) => {
		if (href) {
			router.push(href)
		} else {
			openConsultationModal()
		}
	}

	return (
		<section className={styles.itProduct}>
			<AnimatedSection animation='slideUp'>
				<div className={styles.subtitle}>Готовые ИТ-продукты</div>
				<h2 className={styles.title}>Автоматизация бизнес-процессов и обработка данных</h2>
			</AnimatedSection>

			<AnimatedSection animation='slideUp'>
				<div className={styles.categories}>
					<div className={styles.embla__viewport} ref={emblaRef}>
						<div className={styles.embla__container}>
							{CATEGORIES.map((category) => (
								<div key={category} className={styles.embla__slide}>
									<button
										className={`${styles.categoryButton} ${
											activeCategory === category ? styles.categoryButtonActive : ''
										}`}
										onClick={() => setActiveCategory(category)}
									>
										{category}
									</button>
								</div>
							))}
						</div>
					</div>
				</div>
			</AnimatedSection>

			<div className={styles.grid}>
				{visibleCards.map((card) => (
					<AnimatedSection key={card.title} animation='slideUp' className={styles.gridItem}>
						<div className={styles.card}>
							<div className={styles.cardImage}>
								<Image
									src={card.image}
									alt={card.title}
									width={318}
									height={120}
									className={styles.image}
									unoptimized
								/>
							</div>
							<div className={styles.cardBody}>
								<div className={styles.badges}>
									{card.badges.map((badge) => (
										<span key={badge} className={`${styles.badge} ${BADGE_CLASS[badge]}`}>
											{badge}
										</span>
									))}
								</div>
								<h3 className={styles.cardTitle}>{card.title}</h3>
								<p className={styles.cardDescription}>{card.description}</p>
								<div className={styles.link}>
									<div className={styles.linkContent} onClick={() => handleCardClick(card.href)}>
										<span className={styles.linkText}>Подробнее</span>
										<div className={styles.linkIcon}>
											<svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
												<path
													d='M6.33333 3V4.33333H10.7267L3 12.06L3.94 13L11.6667 5.27333V9.66667H13V3H6.33333Z'
													fill='#f23b67'
												/>
											</svg>
										</div>
									</div>
								</div>
							</div>
						</div>
					</AnimatedSection>
				))}
			</div>

			{showButton && (
				<button className={styles.showMoreButton} onClick={handleShowMore}>
					<span className={styles.showMoreText}>Показать ещё</span>
					<svg
						className={styles.showMoreIcon}
						width='8'
						height='5'
						viewBox='0 0 8 5'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'
					>
						<path d='M0.94 0L4 3.05333L7.06 0L8 0.94L4 4.94L0 0.94L0.94 0Z' fill='white' />
					</svg>
				</button>
			)}
		</section>
	)
}

ItProduct.displayName = 'ItProduct'

export default ItProduct
